import { useCallback } from "react"
import { v4 as uuidv4 } from "uuid"
import { useSetRecoilState, useRecoilValue, useRecoilState } from "recoil"
import {
  NotificationResponse,
  ModelUpdatingResponse,
  DataRequestResponse,
  HeartbeatResponse
} from "./types"
import * as Util from "../util"
import { ConsoleLogger } from "../util/logger"
import { chatIdsSelector } from "../recoil/atoms/chats"
import { activeSpeakerSelector } from "../recoil/atoms/activeSpeaker"
import { roomCallbackSelector } from "../recoil/atoms/room"
import { viewSelector } from "../recoil/atoms/view"
import { IS_RECORDING_VIEW, Routes, Views } from "../constants"
import { joinButtonLoadingSelector } from "../recoil/atoms/joinButton"
import {
  playerPropsSelector,
  currentTimeMNPSelector
} from "../recoil/atoms/player"
import { recordingSelector } from "../recoil/atoms/recording"
import { streamAllStatusSelector } from "../recoil/atoms/streams"
import { useModalUpdate } from "./useModalUpdate"
import { useDataRequest } from "./useDataRequest"
import { localUserCallbackSelector } from "../recoil/atoms/users"
import useServerWatchParty from "./useServerWatchParty"
import {
  audienceTotalCountSelector,
  broadcasterTotalCountSelector,
  moderatorTotalCountSelector
} from "../recoil/atoms/userCounts"
import { startAudienceDisconnectTimer } from "../util/disconnectAudienceUser"
import { privateRoomSelector } from "../recoil/atoms/privateRoom"
import { taggedBadgesAtom } from "../recoil/atoms/taggedBadges"
import { shoppableMNPVideoAtom } from "../recoil/atoms/shoppableVideos"
import { AppStatus, STREAM_RECORD_ACTION } from "../constants/enums"
import React from "react"
import { SDKContext } from "./SDKContext"
import { useLocalStorage } from "src/hooks/useLocalStorage"
import { useHistory } from "react-router-dom"
import { loadingRoomHashAtom } from "src/recoil/atoms/shows"
import useCustomResetRecoilState from "src/hooks/useCustomResetRecoilState"

interface WsEventsCBProps {
  handleMediaConnect: () => void
  handleNotification: (response: NotificationResponse) => void
  handleModelUpdate: (response: ModelUpdatingResponse) => void
  handleDataRequest: (response: DataRequestResponse) => void
  handleRecordingRequest: (response: DataRequestResponse) => void
  handleStreamingRequest: (response: DataRequestResponse) => void
  handleHeartbeatUpdate: (response: HeartbeatResponse) => void
  handlePrivateRoomRequest: (response: DataRequestResponse) => void
}

const logger = new ConsoleLogger()
const MeetOnline = window.MeetOnline
const ModelType = MeetOnline?.ModelType
const DataRequestType = MeetOnline?.DataRequestType
export const useEventsCB = (): WsEventsCBProps => {
  const roomDataProps = useRecoilValue(roomCallbackSelector)
  const {
    doAuctionUpdate,
    doChatUpdate,
    doOverlayUpdate,
    doPollUpdate,
    doRoomUpdate,
    doStreamConfigUpdate,
    doUserUpdate,
    doWatchPartyConfigUpdate,
    doProductsUpdate,
    doMediaUpdate
  } = useModalUpdate()

  const {
    doStreamConfigUpdateForDataRequest,
    doAuctionUpdateForDataRequest,
    doChatsUpdateForDataRequest,
    doOverlaysUpdateForDataRequest,
    doPollsUpdateForDataRequest,
    doProductsUpdateForDataRequest,
    doWatchPartyLinksUpdateForDataRequest
    // doUsersUpdateForDataRequest
  } = useDataRequest()
  const setJoinButtonLoading = useSetRecoilState(joinButtonLoadingSelector)
  const setChats = useSetRecoilState(chatIdsSelector)
  const setShoppableMNPVideo = useSetRecoilState(shoppableMNPVideoAtom)
  const setTaggedBadges = useSetRecoilState(taggedBadgesAtom)
  const setPlayerProps = useSetRecoilState(playerPropsSelector)
  const setCurrentTimeMNPProps = useSetRecoilState(currentTimeMNPSelector)
  const setStreaming = useSetRecoilState(streamAllStatusSelector)
  const localUserCallback = useRecoilValue(localUserCallbackSelector)
  const setAudienceCount = useSetRecoilState(audienceTotalCountSelector)
  const setBroadcasterCount = useSetRecoilState(broadcasterTotalCountSelector)
  const setModeratorCount = useSetRecoilState(moderatorTotalCountSelector)
  const setPrivateRoomInfo = useSetRecoilState(privateRoomSelector)
  const meetingClient = React.useContext(SDKContext)
  const [returnToHashStorage, returnToHashParseStorage, setReturnToHash] =
    useLocalStorage("returnToHash")
  const [loading, setLoading] = useRecoilState(joinButtonLoadingSelector)
  const [loadingRoomHash, setLoadingRoomHash] =
    useRecoilState(loadingRoomHashAtom)
  const history = useHistory()
  const resetRecoilState = useCustomResetRecoilState()

  const setRecording = useSetRecoilState(recordingSelector)
  const setView = useSetRecoilState(viewSelector)
  const setActiveSpeaker = useSetRecoilState(activeSpeakerSelector)
  const {
    stopServerWatchParty,
    startServerWatchParty,
    setWatchPartyScreenProps
  } = useServerWatchParty()

  const handleMediaConnect = () => {
    if (MeetOnline?.client().maxHostsVisibleToAudience === true) {
      startServerWatchParty()
    }

    let activeSpeakerUid: string = MeetOnline?.client()
      .maxHostsVisibleToAudience
      ? MeetOnline?.client().maxHostsVisibleToAudience
      : ""
    setActiveSpeaker({ uid: activeSpeakerUid })

    if (IS_RECORDING_VIEW) {
      setView(Views.RECORDING_VIEW)
    } else {
      setView(Views.MEETING_ROOM)
    }

    setJoinButtonLoading(false)
  }

  const handleNotification = async (resp: NotificationResponse) => {
    const localUser = await localUserCallback.handleReturnData()
    const _roomData = await roomDataProps.handleReturnData()
    let wpHostId = MeetOnline?.client().rtc?.getWatchPartyHostId()
    // if previous watch party was SERVER HOSTED WATCH PARTY, then STOP that
    if (
      (wpHostId === "modNotPresentImage" ||
        wpHostId === "modNotPresentVideo" ||
        wpHostId === "modNotPresentOverlay") &&
      (resp.value.enabled === true ||
        resp.value.mediaProductProps.enabled === true)
    ) {
      stopServerWatchParty()
    }
    if (resp.notification === "WP_STATUS_UPDATE") {
      try {
        setWatchPartyScreenProps(
          resp.value.enabled,
          resp.value.src,
          resp.value.hostId
        )

        if (resp.value.enabled === false) {
          setPlayerProps({
            isControlVolume: false,
            isEnabledToggleControlDesktop: false
          })
          setShoppableMNPVideo({
            srcShoppableMNP: "",
            enabledShoppableMNP: false,
            idShoppableMNP: "",
            typeShoppableMNP: ""
          })
          setTaggedBadges([])
          window.MeetOnline?.client().rtc?.setMediaProductProps({
            id: "",
            productsIds: null,
            enabled: false
          })
        }

        if (
          resp.value.mediaProductProps.enabled === true &&
          resp.value.enabled === true
        ) {
          const { id } = resp.value.mediaProductProps

          setShoppableMNPVideo({
            srcShoppableMNP: "",
            enabledShoppableMNP: true,
            idShoppableMNP: id,
            typeShoppableMNP: "share"
          })
        }

        if (resp.value.enabled === false && resp.value.src === "") {
          setCurrentTimeMNPProps({ type: "SAVE_LAST_TIME" })
        }

        if (
          resp.value.enabled === false &&
          resp.value.src === "" &&
          resp.value.hostId !== localUser.id
        ) {
          setPlayerProps({
            isMNPLastTime: true
          })
        }
        MeetOnline?.client().rtc?.handleWatchPartyStatusUpdate(resp.value)

        if (
          resp.value.mediaProductProps.enabled === true &&
          resp.value.enabled === true
        ) {
          // loading product data after start watch party
          const { productsIds } = resp.value.mediaProductProps
          const productsData = await MeetOnline?.client().fetchProductByIds(
            productsIds
          )
          setTaggedBadges(productsData)
        }

        if (resp.value.enabled === false && resp.value.src === "") {
          setPlayerProps({
            isMNPLastTime: true
          })
          if (_roomData.showInteractivityRequiredVisual) {
            startServerWatchParty()
          }
        }
      } catch (e) {
        logger.error(
          "------- Exception in SDKContext::handleNotification -------",
          e
        )
      }
    }
  }

  const handleRecordingRequest = (response: DataRequestResponse) => {
    if (response.status === "OK") {
      setRecording({ type: "TOGGLE", payload: response })
    } else {
      logger.error("ERROR: handleRecordingRequest")
      setRecording({
        type: "TOGGLE",
        payload: { action: STREAM_RECORD_ACTION.STOP }
      })
    }
  }

  const handleStreamingRequest = (response: DataRequestResponse) => {
    if (response.status === "OK") {
      setStreaming({ type: "TOGGLE", payload: response })
    } else {
      logger.error("ERROR: handleStreamingRequest")
      setStreaming({
        type: "TOGGLE",
        payload: { action: STREAM_RECORD_ACTION.STOP }
      })
    }
  }

  const handleDataRequest = (response: DataRequestResponse) => {
    logger.log("SDKContext INDEX.JS handleDataRequest - response = ", response)
    if (response.status !== "OK") {
      logger.error("ERROR")
      return
    }

    switch (response.dataRequestType) {
      /* case DataRequestType.USERS:
        doUsersUpdateForDataRequest(response)
        break */
      case DataRequestType.CHAT:
        doChatsUpdateForDataRequest(response)
        break
      case DataRequestType.POLL:
        doPollsUpdateForDataRequest(response)
        break
      case DataRequestType.WATCH_PARTY_CONFIG:
        doWatchPartyLinksUpdateForDataRequest(response)
        break
      case DataRequestType.STREAM_CONFIG:
        doStreamConfigUpdateForDataRequest(response)
        break
      case DataRequestType.PRODUCTS:
        doProductsUpdateForDataRequest(response)
        break
      case DataRequestType.OVERLAY:
        doOverlaysUpdateForDataRequest(response)
        break
      case DataRequestType.AUCTION:
        doAuctionUpdateForDataRequest(response)
        break
      default:
        logger.log("Invalid dataRequestType = " + response.dataRequestType)
    }
  }

  const handleModelUpdate = async (response: ModelUpdatingResponse) => {
    const _roomData = await roomDataProps.handleReturnData()
    const localUser = await localUserCallback.handleReturnData()

    if (response.status !== "OK" && response.message === "Bad words present") {
      const message = {
        id: `bad-message-${uuidv4()}`,
        badMessage: true,
        author: {
          id: localUser?.id,
          name: localUser?.name,
          avatar: localUser?.avatar,
          color: localUser?.color
        },
        chatStatus: "AUTO_APPROVED",
        chatType: "",
        message: response.chatMessage,
        published: false,
        participants: [],
        targetUserId: "",
        replyChats: [],
        reactions: [],
        timestamp: Util.UTCTime()
      }

      const reply = {
        id: `bad-reply-${uuidv4()}`,
        badReply: true,
        author: {
          id: localUser?.id,
          name: localUser?.name,
          avatar: localUser?.avatar,
          color: localUser?.color
        },
        chatStatus: "AUTO_APPROVED",
        chatType: "",
        parentChatId: response.parentChatId,
        message: response.chatMessage,
        published: false,
        participants: [],
        targetUserId: "",
        reactions: [],
        timestamp: Util.UTCTime()
      }

      if (response.parentChatId !== undefined) {
        //TODO need fix type
        //@ts-ignore
        setChats({ type: "ADD_BAD_REPLY", payload: { message: reply } })
      } else {
        //TODO need fix type
        //@ts-ignore
        setChats({ type: "ADD_BAD_MESSAGE", payload: { message: message } })
      }
    }
    if (response.status !== "OK") {
      if (response.message === "Missing user entitlement") {
        // redirect to another page
        const roomHash = meetingClient?.getRoomHash()
        setReturnToHash({ roomHash }, "rewrite")
        await meetingClient?.disconnectGateway(0)
        setLoading(false) // TODO: make the home form not have a global state if possible
        setLoadingRoomHash(null)
        resetRecoilState()
        setView(Views.LOADING)
        history.push(Routes.PLANS)
        return
      }
      logger.error("ERROR")
      return
    }
    if (!_roomData) {
      logger.error("SDKContext => Invalid Room")
      return
    }

    if (
      response.roomSessionId !== undefined &&
      response.roomSessionId !== _roomData.roomSessionId &&
      !_roomData.branding.modNotPresentEventTracker
    ) {
      logger.error("SDKContext => Invalid Room State")
      return
    }

    switch (response.model) {
      case ModelType.user:
        doUserUpdate(response)
        break

      case ModelType.room:
        doRoomUpdate(response)
        break

      case ModelType.chat:
        doChatUpdate(response)
        break
      case ModelType.overlay:
        doOverlayUpdate(response)
        break
      case ModelType.media:
        doMediaUpdate(response)
        break
      case ModelType.poll:
        doPollUpdate(response)
        break
      case ModelType.streamConfig:
        doStreamConfigUpdate(response)
        break
      case ModelType.auction:
        doAuctionUpdate(response)
        break
      case ModelType.watchPartyConfig:
        doWatchPartyConfigUpdate(response)
        break
      case ModelType.products:
        doProductsUpdate(response)
        break
      default:
        logger.log("Invalid Model - " + response.model)
    }
  }

  const handleHeartbeatUpdate = useCallback(
    (response: HeartbeatResponse) => {
      if (response?.userCounts) {
        setAudienceCount(response.userCounts.audienceCount)
        setBroadcasterCount(response.userCounts.broadcasterCount)
        setModeratorCount(response.userCounts.moderatorCount)
        // start or restart the timer
        startAudienceDisconnectTimer(response.userCounts)
      }
    },
    [setAudienceCount]
  )

  const handlePrivateRoomRequest = useCallback(
    (response: DataRequestResponse) => {
      setPrivateRoomInfo({ action: "OPEN", data: response })
    },
    [setPrivateRoomInfo]
  )

  return {
    handleMediaConnect,
    handleNotification,
    handleModelUpdate,
    handleDataRequest,
    handleRecordingRequest,
    handleStreamingRequest,
    handleHeartbeatUpdate,
    handlePrivateRoomRequest
  }
}
